import { useSwaggerlessContext } from 'Context/SwaggerlessContext';
import React, { useState, useEffect } from 'react';
import DataTable from 'Components/UI/DataTable';
import 'Components/AdvisorApp/Viewport/Users/UserGrid/index.css';
import FontAwesome from 'Components/UI/FontAwesome/FontAwesome';
import AgentDialog from 'Components/AdvisorApp/Viewport/Users/AgentDialog';
import Agent, {
  fromAPIResponse,
} from 'Classes/Agent';
import { addSpacesBetweenWords } from 'Helpers/Formats';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import {
  PlannerTransitionDialog,
} from 'Components/AdvisorApp/Viewport/Users/PlannerTransitionDialog/PlannerTransitionDialog';
import fr from 'facet-rest';
import { FDSecondaryActionIconButton } from '@facet-wealth/design';
import {
  OnboardingCapacityDialog,
} from 'Components/AdvisorApp/Viewport/Users/OnboardingCapacityDialog/OnboardingCapacityDialog';

// Override ReactTable's default filter function (they use exact match).
// This filters for case-insensitive matches.
const filterFunc = (filter, row) => {
  const cellValue = row[filter.id];
  if (cellValue === undefined) {
    return false;
  }
  return cellValue.toLowerCase().includes(filter.value.toLowerCase());
};

const UserGrid = () => {
  const { getAgents } = useSwaggerlessContext();
  const [isAddFlyoutOpen, toggleAddFlyout] = useState(false);
  const [isEditFlyoutOpen, toggleEditFlyout] = useState(false);
  const [plannerTransitionDialogOpen, setPlannerTransitionDialogOpen] = useState(false);
  const [plannerTransitionRelationship, setPlannerTransitionRelationship] = useState(null);
  const [onboardingCapacityDialogOpen, setOnboardingCapacityDialogOpen] = useState(false);
  const [onboardingCapacityRelationship, setOnboardingCapacityRelationship] = useState(null);
  const [agentToEdit, setAgentToEdit] = useState(new Agent());
  const [users, setUsers] = useState([]);

  const refreshTable = () => {
    getAgents()
      .then((res) => {
        if (
          !res.data
          || !res.data.attributes
          || !res.data.attributes.agents
          || !Array.isArray(res.data.attributes.agents)
        ) {
          Promise.reject(new Error('malformed response'));
        }
        const agents = res.data.attributes.agents.map(agent => fromAPIResponse(agent));
        setUsers(agents);
      })
      .catch((err) => {
        console.error(err); // eslint-disable-line
        displayErrorMessage(err, 'Failed to fetch users');
      });
  };

  useEffect(() => {
    refreshTable();
  }, []);

  const columns = [
    {
      id: 'name',
      Header: () => <div className="data-table-header">Name</div>,
      accessor: d => `${d.firstName} ${d.lastName}`,
      filterMethod: filterFunc,
    },
    {
      id: 'email',
      Header: () => <div className="data-table-header">Email</div>,
      accessor: d => d.email,
      filterMethod: filterFunc,
    },
    {
      id: 'zoomUrl',
      Header: () => <div className="data-table-header">Zoom URL</div>,
      accessor: d => d.zoomUrl,
      filterMethod: filterFunc,
    },
    {
      id: 'teams',
      Header: () => <div className="data-table-header">Teams</div>,
      accessor: d => (d.teamCoach ? `${d.teamCoach.firstName} ${d.teamCoach.lastName}` : ''),
      filterMethod: filterFunc,
    },
    {
      id: 'role',
      Header: () => <div className="data-table-header">Role</div>,
      accessor: d => addSpacesBetweenWords(d.role),
      filterMethod: filterFunc,
    },
    {
      id: 'date-created',
      Header: () => <div className="data-table-header">Date Created</div>,
      accessor: d => d.createdAt.format('MM/DD/YYYY'),
      filterMethod: filterFunc,
      maxWidth: 110,
    },
    {
      id: 'agent-id',
      Header: () => <div className="data-table-header">Agent ID</div>,
      accessor: d => d.id.toString(),
      filterMethod: filterFunc,
      maxWidth: 100,
    },
    {
      id: 'onboarding-status',
      accessor: d => d.id.toString(),
      Cell: row => {
        return row.original.resource.withRelationship(fr.relationships.onboardingCapacity.name,
          (rel) => (
            <FDSecondaryActionIconButton
              disablePadding
              icon="calendarDays"
              size={'sm'}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOnboardingCapacityDialogOpen(true);
                setOnboardingCapacityRelationship(rel);
              }}
            />
          ),
          () => null);
      },
      filterable: false,
      sortable: false,
      maxWidth: 60,
    },
  ];


  return (
    <div className="user-grid">
      <div className="action-bar">
        <div className="user-count">{`${users.length} Users`}</div>
        <div
          className="add-user"
          test="add-user-btn"
          onClick={() => toggleAddFlyout(!isAddFlyoutOpen)}
          role="presentation"
        >
          <FontAwesome icon="plus-circle" />
          Add User
        </div>
      </div>
      <DataTable
        className="-striped"
        data={users}
        columns={columns}
        minRows={10}
        pageSize={users.length}
        pages={1}
        filterable
        showPagination={false}
        getTdProps={(state, rowInfo) => ({
          onClick: (e, handleOriginal) => {
            // rowInfo.original is the object that was used to populate that table row.
            // In this case, an agent.
            if (rowInfo && rowInfo.original) {
              setAgentToEdit(rowInfo.original);
              toggleEditFlyout(true);
            }

            // IMPORTANT! React-Table uses onClick internally to trigger
            // events like expanding SubComponents and pivots.
            // By default a custom 'onClick' handler will override this functionality.
            // If you want to fire the original onClick handler, call the
            // 'handleOriginal' function.
            if (handleOriginal) {
              handleOriginal();
            }
          },
          style: {
            cursor: 'pointer',
          },
        })}
      />
      {isAddFlyoutOpen && (
        <AgentDialog
          handleClose={() => {
            toggleAddFlyout(!isAddFlyoutOpen);
          }}
          refresh={() => refreshTable()}
          isAdd
        />
      )}
      {isEditFlyoutOpen && (
        <AgentDialog
          agentProp={agentToEdit}
          handleClose={() => toggleEditFlyout(!isEditFlyoutOpen)}
          openPlannerTransitionDialog={(rel) => {
            // Based on the current use, rel will always exist, but it's not tightly coupled so this check is here for safety
            if (rel !== undefined) {
              setPlannerTransitionRelationship(rel);
              toggleEditFlyout(false);
              setPlannerTransitionDialogOpen(true);
            }
          }}
          refresh={() => refreshTable()}
          isAdd={false}
        />
      )}
      {plannerTransitionRelationship && (
        <PlannerTransitionDialog
          plannerTransitionRelationship={plannerTransitionRelationship}
          open={plannerTransitionDialogOpen}
          onClose={() => {
            setPlannerTransitionRelationship(null);
            setPlannerTransitionDialogOpen(false);
          }}
        />
      )}
      {onboardingCapacityRelationship && (
        <OnboardingCapacityDialog
          onboardingCapacityRelationship={onboardingCapacityRelationship}
          open={onboardingCapacityDialogOpen}
          onClose={() => {
            setOnboardingCapacityRelationship(null);
            setOnboardingCapacityDialogOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default UserGrid;
